import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AcepActeur } from 'app/modules/models/acepActeur.type';
import { StorageItem } from 'app/modules/models/storageItem.type';
import { environment } from 'environments/environment';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { NIL } from 'uuid';

const PROFIL_BENEFICIAIRE_CODE = 'PARTBN'; // « Bénéficiaire BtoB » : pas un profil de partenaire ciblable

interface SupervisedMandataireResponse {
    mandataire: AcepActeur | null;
    isActive?: boolean;
    isDeleted?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class PartnersService {

    protected apiRoute = environment.RESOURCE_API;
    protected teraApiRoute = environment.TERA_RESOURCE_API;

    private _currentPartnerSubject = new BehaviorSubject<AcepActeur | null>(null);

    setCurrentPartner(partner: AcepActeur) {
        this._currentPartnerSubject.next(partner);
    }

    getCurrentPartner() {
        return this._currentPartnerSubject.asObservable();
    }

    constructor(private _httpClient: HttpClient) { }

    notifyDropFolder(dto: any): Observable<AcepActeur> {
        const route = this.apiRoute + environment.apiCatalog.partners.controllerRoute + environment.apiCatalog.partners.subRoutes.notifyDropPartnerFolder;
        return this._httpClient.post(route, dto, this.prepareHeader()).pipe(map(res => res as AcepActeur))
    }

    getDropHistory(companyId: string): Observable<StorageItem[]> {
        const route = this.apiRoute + environment.apiCatalog.partners.controllerRoute + environment.apiCatalog.partners.subRoutes.getDropHistory + companyId;
        return this._httpClient.get(route, this.prepareHeader()).pipe(map(res => res as StorageItem[]))
    }

    getProjectHistory(partnerId?: string, mandateId?: string): Observable<any[]> {
        let params = new HttpParams();
        if (partnerId) params = params.set('partnerId', partnerId);
        if (mandateId) params = params.set('mandateId', mandateId);
    
        const route = this.apiRoute + environment.apiCatalog.partners.controllerRoute + environment.apiCatalog.partners.subRoutes.getProjectHistory;
        return this._httpClient.get(route, { ...this.prepareHeader(), params }).pipe(map(res => res as any[]));
    }

    getPartnerByCompanyId(companyId: string): Observable<AcepActeur> {
        const route = this.apiRoute + environment.apiCatalog.partners.controllerRoute + '/' + companyId;
        return this._httpClient.get<AcepActeur>(route, this.prepareHeader());
    }

    getActorsByType(type: string): Observable<AcepActeur[]> {
        const route = this.teraApiRoute + environment.teraApiCatalog.GET_ACTORS_BY_TYPE_API + type;
        return this._httpClient.get(route, this.prepareHeader()).pipe(map(res => res as AcepActeur[]))
    }

    //TODO REMOVE DETTE
    getTeraActorById(actorId: string): Observable<any> {
        const route = this.teraApiRoute + environment.teraApiCatalog.GET_ACTOR_BY_ID_API + actorId;
        return this._httpClient.get(route, this.prepareHeader());
    }

    // Profils métier TERA des partenaires : paramètres de type PROFIL rattachés au type d'acteur AC2 (Partenaires), hors bénéficiaire
    getPartnerBusinessProfiles(): Observable<{ id: string; code: string; name: string }[]> {
        const route = this.teraApiRoute + environment.teraApiCatalog.GET_PARAMETERS_BY_CODE_TYPE_API + 'PROFIL/' + NIL;
        return this._httpClient.get(route, this.prepareHeader()).pipe(
            map((parameters: any[]) => parameters.filter(parameter =>
                parameter.parent?.code === 'AC2' && parameter.code !== PROFIL_BENEFICIAIRE_CODE))
        );
    }

    getPartnersByMandate(mandateId): Observable<AcepActeur[]> {
        const route = this.teraApiRoute + environment.teraApiCatalog.GET_PARTNERS_BY_MANDATES_API + mandateId;
        return this._httpClient.get(route, this.prepareHeader()).pipe(map(res => res as AcepActeur[]))
    }

    getMandatairesBySuperviseur(superviseurGuid: string): Observable<AcepActeur[]> {
        const route = this.teraApiRoute + environment.teraApiCatalog.GET_MANDATAIRES_BY_SUPERVISEUR_API + superviseurGuid;
        return this._httpClient.get<Array<SupervisedMandataireResponse | AcepActeur>>(route, this.prepareHeader()).pipe(
            map((response) => (response ?? [])
                .map((item) => {
                    const relation = item as SupervisedMandataireResponse;
                    if (!relation.mandataire) {
                        return item as AcepActeur;
                    }

                    return {
                        ...relation.mandataire,
                        isActive: relation.mandataire.isActive ?? relation.isActive,
                        isDeleted: (relation.mandataire as any).isDeleted ?? relation.isDeleted
                    } as AcepActeur;
                })
                .filter((mandataire): mandataire is AcepActeur => !!mandataire)
            )
        );
    }

    getMandatairesByPartner(partnerId): Observable<AcepActeur[]> {
        const route = this.teraApiRoute + environment.teraApiCatalog.GET_MANDATAIRES_BY_PARTNER_API + partnerId;
        return this._httpClient.get(route, this.prepareHeader()).pipe(map(res => res as AcepActeur[]))
    }

    private prepareHeader(): Object {
        let headers = new HttpHeaders();
        headers = headers.set('Accept', 'application/json');
        return { headers: headers };
    }
}
